/* ==========================================================================
   Dr. Mark Leong — Wellness Promo Landing Page
   Lightweight, dependency-free enhancements:
   1. Sticky mobile CTA (hides when product grid / final CTA are on screen)
   2. Review slider prev/next buttons (layout itself is CSS scroll-snap)
   3. Subtle scroll-reveal (respects prefers-reduced-motion)
   4. Smooth in-page anchor scrolling for CTA links
   Everything is idempotent so Theme Editor section reloads re-initialise
   cleanly (listens to shopify:section:load).
   ========================================================================== */
(function () {
  'use strict';

  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------------------------------------------------------------
     1. Sticky mobile CTA
     Hidden while any element with [data-wlp-sticky-hide-when-visible]
     (product grid, final CTA) is in the viewport, and shown only after
     the visitor scrolls past the hero.
  --------------------------------------------------------------- */
  function initStickyCta() {
    var bar = document.querySelector('[data-wlp-sticky-cta]');
    if (!bar || bar.dataset.wlpInit === 'true') return;
    bar.dataset.wlpInit = 'true';

    var hero = document.querySelector('[data-wlp-hero]');
    var hideTargets = document.querySelectorAll('[data-wlp-sticky-hide-when-visible]');
    var showImmediately = bar.hasAttribute('data-wlp-show-immediately');
    var pastHero = showImmediately || !hero;
    var overlappingCount = 0;

    function update() {
      var show = pastHero && overlappingCount === 0;
      bar.classList.toggle('is-visible', show);
      bar.setAttribute('aria-hidden', show ? 'false' : 'true');
    }

    if ('IntersectionObserver' in window) {
      if (hero && !showImmediately) {
        new IntersectionObserver(function (entries) {
          pastHero = !entries[0].isIntersecting;
          update();
        }, { threshold: 0 }).observe(hero);
      }

      if (hideTargets.length) {
        var visObserver = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            entry.target.__wlpVisible = entry.isIntersecting;
          });
          overlappingCount = 0;
          hideTargets.forEach(function (t) {
            if (t.__wlpVisible) overlappingCount += 1;
          });
          update();
        }, { threshold: 0.15 });
        hideTargets.forEach(function (t) { visObserver.observe(t); });
      }
    } else {
      // Fallback: always show after a small scroll.
      window.addEventListener('scroll', function () {
        pastHero = window.scrollY > 400;
        update();
      }, { passive: true });
    }

    update();
  }

  /* ---------------------------------------------------------------
     2. Review slider controls
  --------------------------------------------------------------- */
  function initSliders(scope) {
    (scope || document).querySelectorAll('[data-wlp-slider]').forEach(function (wrapper) {
      if (wrapper.dataset.wlpInit === 'true') return;
      wrapper.dataset.wlpInit = 'true';

      var track = wrapper.querySelector('[data-wlp-slider-track]');
      var prev = wrapper.querySelector('[data-wlp-slider-prev]');
      var next = wrapper.querySelector('[data-wlp-slider-next]');
      if (!track) return;

      function step() {
        var card = track.firstElementChild;
        return card ? card.getBoundingClientRect().width + 16 : 320;
      }

      function scrollByStep(direction) {
        track.scrollBy({
          left: step() * direction,
          behavior: reducedMotion ? 'auto' : 'smooth'
        });
      }

      if (prev) prev.addEventListener('click', function () { scrollByStep(-1); });
      if (next) next.addEventListener('click', function () { scrollByStep(1); });
    });
  }

  /* ---------------------------------------------------------------
     3. Scroll reveal
  --------------------------------------------------------------- */
  function initReveal(scope) {
    var items = (scope || document).querySelectorAll('.wlp-reveal:not(.wlp-revealed)');
    if (!items.length) return;

    if (reducedMotion || !('IntersectionObserver' in window)) {
      items.forEach(function (el) { el.classList.add('wlp-revealed'); });
      return;
    }

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('wlp-revealed');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -40px 0px' });

    items.forEach(function (el) { observer.observe(el); });
  }

  /* ---------------------------------------------------------------
     4. Smooth in-page anchors (e.g. "See What You'll Receive")
  --------------------------------------------------------------- */
  function initAnchors(scope) {
    (scope || document).querySelectorAll('a[data-wlp-scroll]').forEach(function (link) {
      if (link.dataset.wlpInit === 'true') return;
      link.dataset.wlpInit = 'true';

      link.addEventListener('click', function (event) {
        var href = link.getAttribute('href') || '';
        if (href.charAt(0) !== '#') return;
        var target = document.querySelector(href);
        if (!target) return;
        event.preventDefault();
        target.scrollIntoView({
          behavior: reducedMotion ? 'auto' : 'smooth',
          block: 'start'
        });
        target.setAttribute('tabindex', '-1');
        target.focus({ preventScroll: true });
      });
    });
  }

  /* ---------------------------------------------------------------
     5. Hide external floating promo tabs on this landing page only
  --------------------------------------------------------------- */
  function initPromoFloatingTabHider() {
    if (!document.querySelector('[data-wlp-hero]')) return;

    function hidePromoFloatingTabs() {
      var viewportWidth = window.innerWidth || document.documentElement.clientWidth || 0;
      var promoPhrases = [
        'claim 10% off',
        '10% off',
        '10 off',
        'newsletter'
      ];
      var selectors = [
        "[aria-label*='Claim 10% Off']",
        "[title*='Claim 10% Off']",
        "iframe",
        "a",
        "button",
        "[role='button']"
      ];

      var seen = new Set();

      selectors.forEach(function (selector) {
        document.querySelectorAll(selector).forEach(function (node) {
          if (seen.has(node)) return;
          seen.add(node);

          var text = (node.textContent || '').replace(/\s+/g, ' ').trim().toLowerCase();
          var label = ((node.getAttribute('aria-label') || '') + ' ' + (node.getAttribute('title') || ''))
            .replace(/\s+/g, ' ')
            .trim()
            .toLowerCase();
          var isIframe = node.tagName === 'IFRAME';

          if (isIframe) {
            var iframeRect = typeof node.getBoundingClientRect === 'function' ? node.getBoundingClientRect() : null;
            var iframeStyle = window.getComputedStyle ? window.getComputedStyle(node) : null;
            var iframeLikelyPromo =
              iframeRect &&
              iframeRect.width > 40 &&
              iframeRect.width < 180 &&
              iframeRect.height > 120 &&
              iframeRect.right >= viewportWidth - 40 &&
              iframeRect.top > 80 &&
              iframeRect.top < window.innerHeight - 40 &&
              iframeStyle &&
              iframeStyle.position === 'fixed';

            if (iframeLikelyPromo) {
              node.style.display = 'none';
              if (node.parentElement) node.parentElement.style.display = 'none';
            }

            return;
          }

          var matchesPromoPhrase = promoPhrases.some(function (phrase) {
            return text.includes(phrase) || label.includes(phrase);
          });

          if (!matchesPromoPhrase) return;

          var container =
            node.closest("[style*='position: fixed']") ||
            node.closest("[style*='position:fixed']") ||
            node.closest("[class*='sticky']") ||
            node.closest("[class*='popup']") ||
            node.closest("[class*='embed']") ||
            node.closest('div');

          if (container) {
            container.style.display = 'none';
          } else {
            node.style.display = 'none';
          }
        });
      });
    }

    hidePromoFloatingTabs();
    window.setTimeout(hidePromoFloatingTabs, 400);
    window.setTimeout(hidePromoFloatingTabs, 1200);
    window.setTimeout(hidePromoFloatingTabs, 2400);

    if ('MutationObserver' in window && document.body) {
      var pending = false;
      var observer = new MutationObserver(function () {
        if (pending) return;
        pending = true;

        window.setTimeout(function () {
          pending = false;
          hidePromoFloatingTabs();
        }, 180);
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class', 'style', 'aria-label', 'title']
      });

      window.setTimeout(function () {
        observer.disconnect();
      }, 30000);
    }
  }

  function initAll(scope) {
    initStickyCta();
    initSliders(scope);
    initReveal(scope);
    initAnchors(scope);
    initPromoFloatingTabHider();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  } else {
    initAll(document);
  }

  // Re-initialise when merchants edit sections in the Theme Editor.
  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
})();
